import { hostname } from "./api";
import * as fetchUtils from "ra-core/lib/dataProvider/fetch";
import omitBy from 'lodash.omitby';
import { stringify } from 'query-string';
import { useMemo } from "react";
import { validateSupplier } from "../utils/utils";

/**
 * Función auxiliar para normalizar las respuestas de la API.
 * Maneja diferentes formatos de respuesta y los convierte a un formato estándar
 * compatible con React-Admin: { data: [...], total: X }
 */
const normalizeResponseData = (json, resourceName) => {
  // Caso 1: Respuesta es un array directo
  if (Array.isArray(json)) {
    return {
      data: json,
      total: json.length
    };
  }

  // Caso 2: Respuesta es un objeto con propiedad 'data' (formato estándar)
  if (json && json.data) {
    const totalCount = json.total || json.count || (json.data ? json.data.length : 0);

    return {
      data: json.data,
      total: totalCount
    };
  }

  // Caso 3: Otros formatos o respuesta vacía
  console.error(`No valid data received from ${resourceName} endpoint`);
  return {
    data: [],
    total: 0
  };
};

const fetchJson = token => (url, options = {}) => {
  options.user = {
    authenticated: true,
    token: 'Bearer ' + token,
  };
  return fetchUtils.fetchJson(url, options);
};

const countDiff = (o1, o2) =>
  omitBy(o1, (v, k) => o2[k] === v);

const enumTypes = {
  process: ["tier"],
  views: ["product"]
};

const composeFilter = (paramsFilter, resource) => {
  if (!paramsFilter || Object.keys(paramsFilter).length === 0) {
    return null;
  }

  const flatFilter = fetchUtils.flattenObject(paramsFilter);
  const filters = [];

  // processOrigin/* endpoints require the filter field to be prefixed with the
  // process name (e.g. `processing.databaseVersionId`) — without the prefix the
  // backend returns 500.
  const processOriginPrefix =
    typeof resource === 'string' && resource.startsWith('processOrigin/')
      ? resource.split('/')[1]
      : null;

  Object.keys(flatFilter).forEach((key) => {
    const splitKey = key.split('||');
    let field = splitKey[0];
    let ops = splitKey[1];

    if (field.startsWith('_') && field.includes('.')) {
      field = field.split(/\.(.+)/)[1];
    }

    const rawValue = flatFilter[key];
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      return;
    }

    if (processOriginPrefix && field === 'databaseVersionId') {
      field = `${processOriginPrefix}.${field}`;
    }

    let filterStr;
    if (!ops) {
      if (Array.isArray(flatFilter[key])) {
        filterStr = `${field}||$in||${flatFilter[key].join(',')}`;
      } else if (enumTypes[resource] && enumTypes[resource].includes(key)) {
        filterStr = `${field}||$eq||${flatFilter[key]}`;
      }
      else if (
        typeof flatFilter[key] === 'boolean' ||
        typeof flatFilter[key] === 'number' ||
        (
          typeof flatFilter[key] === 'string' &&
          flatFilter[key].match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/)
        )
      ) {
        filterStr = `${field}||$eq||${flatFilter[key]}`;
      } else {
        // Para búsquedas de texto, no incluimos los % ya que el backend los maneja
        filterStr = `${field}||$like||${flatFilter[key]}`;
      }
    } else {
      // Si el operador viene especificado, lo usamos tal cual
      filterStr = `${field}||${ops}||${flatFilter[key]}`;
    }

    filters.push(filterStr);
  });

  return filters;
};

const composeQueryParams = (queryParams = {}) => {
  const params = { ...queryParams };
  if (params.filter && Array.isArray(params.filter) && params.filter.length > 0) {
    // Convertir el array de filtros en un objeto con índices
    const filterObj = params.filter.reduce((acc, filter, index) => {
      acc[`filter[${index}]`] = filter;
      return acc;
    }, {});
    delete params.filter;
    Object.assign(params, filterObj);
  } else {
    delete params.filter;
  }
  return stringify(fetchUtils.flattenObject(params), { skipNull: true });
};


const joinResources = {
  'process': 'preProcesses'
};

export const buildCircularityArticleMetricsUrl = (apiUrl, params) => {
  const { projectId, collection, name, reference, search } =
    params.filter || {};

  const query = new URLSearchParams();
  query.append('limit', params.pagination.perPage);
  query.append('page', params.pagination.page);
  query.append('sort', params.sort.field);
  query.append('order', params.sort.order);
  if (projectId) query.append('projectId', projectId);
  const collections = Array.isArray(collection)
    ? collection
    : collection
    ? [collection]
    : [];
  collections
    .map((value) => String(value).trim())
    .filter(Boolean)
    .forEach((value) => query.append('collection', value));
  if (name) query.append('name', name);
  if (reference) query.append('reference', reference);
  if (search) query.append('search', search);

  return `${apiUrl}/dashboard/circularity-articles?${query.toString()}`;
};

export const normalizeCircularityArticleMetricsResponse = (json) => {
  if (!json || !Array.isArray(json.data) || !Number.isFinite(Number(json.total))) {
    throw new Error('Invalid Circularity article metrics response');
  }
  return { data: json.data, total: Number(json.total) };
};

// Article-impact sub-resources — camelCase names map to article-impact/* API paths
const ARTICLE_IMPACT_RESOURCES = [
  'articleImpactLca',
  'articleImpactEcoscore',
  'articleImpactCircularity',
  'articleImpactEcobalyse',
];

// Mapeo de recursos para casos especiales donde el nombre del recurso es diferente en la API
const resourceMapping = {
  'retailPackaging': 'retailPackaging',
  'extraction': 'process/extraction',
  'processing': 'process/processing',
  'finishing': 'process/finishing',
  'manufacturing': 'process/manufacturing',
  'packaging': 'process/packaging',
  'transport': 'process/transport',
  'trimming': 'process/trimmings',
  'transportModes': 'transportModes',
  'transportDistances': 'transportDistances',
  'processOrigin/extraction': 'processOrigin/extraction',
  'processOrigin/processing': 'processOrigin/processing',
  'processOrigin/finishing': 'processOrigin/finishing',
  'processOrigin/manufacturing': 'processOrigin/manufacturing',
  'processOrigin/packaging': 'processOrigin/packaging',
  'processOrigin/transport': 'processOrigin/transport',
  'processOrigin/trimming': 'processOrigin/trimming',
  // processByAccount/<type>: react-admin resource para la sección "Process by account".
  // Se mapea al mismo endpoint processOrigin/<type> porque la entidad raíz es processOrigin;
  // las asignaciones a cuentas viven como sub-recurso (/processOrigin/<type>/:id/accounts)
  // y se gestionan con fetches directos desde la UI (ver AccountProcessOriginResource.js).
  'processByAccount/extraction': 'processOrigin/extraction',
  'processByAccount/processing': 'processOrigin/processing',
  'processByAccount/finishing': 'processOrigin/finishing',
  'processByAccount/manufacturing': 'processOrigin/manufacturing',
  'processByAccount/packaging': 'processOrigin/packaging',
  'processByAccount/transport': 'processOrigin/transport',
  'processByAccount/trimming': 'processOrigin/trimming',
  'ecom-reference-article': 'ecommerceReference',
  'supplier': 'supplier',
  'materialExtraction': 'materialExtraction',
  'materialProcessing': 'materialProcessing',
  'materialFinishing': 'materialFinishing',
  'articleProcessManufacturing': 'articleManufacturing',
  'articleProcessFinishing': 'articleFinishing',
  'articleProcessTrimming': 'articleTrimming',
  'defaultMaterialFinishing': 'defaultMaterialFinishing',
  'defaultMaterialProcessing': 'defaultMaterialProcessing',
  'rawMaterialCategoryLossRate': 'rawMaterialCategoryLossRate',
  'rawMaterialCategoryMaterialGroup': 'rawMaterialCategoryMaterialGroup',
  // Article Impact: camelCase name → real API path
  'articleImpactLca': 'article-impact/lca',
  'articleImpactEcoscore': 'article-impact/ecoscore',
  // circularity-records es el CRUD del registro almacenado (las lecturas calculadas viven en article-impact/circularity)
  'articleImpactCircularity': 'article-impact/circularity-records',
  'articleImpactEcobalyse': 'article-impact/ecobalyse',
  // Historical tables: map to account-* endpoints
  'articles': 'account-articles',
  'material': 'account-materials',
  'supplier': 'account-suppliers',
  'retailPackaging': 'account-retail-packagings',
  // Versioning system
  'databaseVersion': 'database-version',
  'lcaMethodologyVersion': 'lca-methodology-version',
  'circularityVersion': 'circularity-version',
  'bcomeVersion': 'bcome-version',
  'carbonFactorReleases': 'corporate-carbon/factor-releases',
  // Project tables: separate react-admin resources sharing the same API endpoints
  'articleProject': 'articles',
  'materialProject': 'material',
  'supplierProject': 'supplier',
  'retailPackagingProject': 'retailPackaging',
  'projectSecondaryPackaging': 'project-packagings/secondary',
  'projectTertiaryPackaging': 'project-packagings/tertiary',
  // Article DPP — read-only view over the same project-articles endpoint.
  // List/Show share /articles/{accountId} and /articles/{accountId}/{id}.
  'articleDpp': 'articles',
  // Data Seamless admin screens → /data-seamless/* endpoints
  'businessRulesCatalog': 'data-seamless/business-rules-catalog',
  'processApplicability': 'data-seamless/process-applicability',
  'weightShares': 'data-seamless/weight-shares',
  'fieldDefinitionsCoverage': 'data-seamless/field-definitions/assumption-coverage',
  // Account-scoped: dataProvider appends /:accountId (see resourcesWithAccountId)
  'accountTranslations': 'data-seamless/catalog-aliases',
};

/**
 * Transformador de nomenclatura para ArticleLCA
 * Convierte datos del backend (nomenclatura antigua) a nomenclatura nueva
 */
const transformArticleLcaData = (record) => {
  if (!record) return record;

  // Mapeo de indicadores: nombre antiguo → código nuevo
  const INDICATOR_MAP = {
    Water: 'WS',
    ClimateChange: 'CC',
    Eutrophication: 'EU',
    FossilFuels: 'FF',
    ozoneDepletion: 'OD',
    humanToxicityCancerEffects: 'HTC',
    humanToxicityNonCancerEffects: 'HTNC',
    particulateMatter: 'PM',
    ionizingRadiation: 'IR',
    photochemicalOzoneFormation: 'POF',
    acidification: 'AC',
    terrestrialEutrophication: 'TE',
    marineEutrophication: 'ME',
    freshwaterEcotoxicity: 'FE',
    landUse: 'LU',
    resourceUseMineralAndMetals: 'RM',
    waterConsumption: 'WC',
  };

  // Mapeo de stages: prefijo antiguo → sufijo nuevo
  const STAGE_MAP = {
    // Unitario (sin prefijo)
    '': 'U_A',
    // Stages
    'RawMaterial_': 'RME_U_A',
    'MaterialProc_': 'MP_U_A',
    'MaterialDFP_': 'MF_U_A',
    'ArticleDFP_': 'AF_U_A',
    'Manufacture_': 'M_U_A',
    'Transport_': 'TI_U_A',
    'LaDP_': 'Pack_U_A',
  };

  // Mapeo de Industry Standards: prefijo antiguo → sufijo nuevo
  const IS_MAP = {
    'IndustryStandard_RawMaterial_': 'RME_U_A_IS',
    'IndustryStandard_MaterialProc_': 'MP_U_A_IS',
    'IndustryStandard_MaterialDFP_': 'MF_U_A_IS',
    'IndustryStandard_ArticleDFP_': 'AF_U_A_IS',
    'IndustryStandard_Manufacture_': 'M_U_A_IS',
    'IndustryStandard_Transport_': 'TI_U_A_IS',
    'IndustryStandard_LaDP_': 'Pack_U_A_IS',
  };

  const transformed = { ...record };

  // Transformar cada campo
  Object.keys(record).forEach(oldKey => {
    // Buscar en Industry Standards primero (tienen prefijo más específico)
    for (const [oldPrefix, newSuffix] of Object.entries(IS_MAP)) {
      if (oldKey.startsWith(oldPrefix)) {
        const indicatorOld = oldKey.replace(oldPrefix, '');
        const indicatorNew = INDICATOR_MAP[indicatorOld];
        if (indicatorNew) {
          const newKey = `${indicatorNew}_${newSuffix}`;
          transformed[newKey] = record[oldKey];
          return;
        }
      }
    }

    // Buscar en Stages
    for (const [oldPrefix, newSuffix] of Object.entries(STAGE_MAP)) {
      if (oldPrefix === '') {
        // Caso unitario: campo directo como "Water", "ClimateChange"
        const indicatorNew = INDICATOR_MAP[oldKey];
        if (indicatorNew) {
          const newKey = `${indicatorNew}_U_A`;
          transformed[newKey] = record[oldKey];
          return;
        }
      } else {
        // Casos con prefijo
        if (oldKey.startsWith(oldPrefix)) {
          const indicatorOld = oldKey.replace(oldPrefix, '');
          const indicatorNew = INDICATOR_MAP[indicatorOld];
          if (indicatorNew) {
            const newKey = `${indicatorNew}_${newSuffix}`;
            transformed[newKey] = record[oldKey];
            return;
          }
        }
      }
    }

    // Caso especial: waterConsumption unitario ya viene como WC_U_A en algunos casos
    if (oldKey === 'WC_U_A' || oldKey === 'WC_U_IS') {
      // Ya está en formato correcto, no transformar
      return;
    }
  });

  return transformed;
};

/**
 * Transforms Article LCA data from NEW nomenclature (WS_U_A, WS_RME_U_A, etc.)
 * back to OLD nomenclature (Water, RawMaterial_Water, etc.) for API updates
 */
const reverseTransformArticleLcaData = (record) => {
  if (!record) return record;

  const INDICATOR_REVERSE_MAP = {
    WS: 'Water',
    CC: 'ClimateChange',
    EU: 'Eutrophication',
    FF: 'FossilFuels',
    OD: 'ozoneDepletion',
    HTC: 'humanToxicityCancerEffects',
    HTNC: 'humanToxicityNonCancerEffects',
    PM: 'particulateMatter',
    IR: 'ionizingRadiation',
    POF: 'photochemicalOzoneFormation',
    AC: 'acidification',
    TE: 'terrestrialEutrophication',
    ME: 'marineEutrophication',
    FE: 'freshwaterEcotoxicity',
    LU: 'landUse',
    RM: 'resourceUseMineralAndMetals',
    WC: 'waterConsumption',
  };

  const STAGE_REVERSE_MAP = {
    U_A: '',
    RME_U_A: 'RawMaterial_',
    MP_U_A: 'MaterialProc_',
    MF_U_A: 'MaterialDFP_',
    AF_U_A: 'ArticleDFP_',
    M_U_A: 'Manufacture_',
    TI_U_A: 'Transport_',
    Pack_U_A: 'LaDP_',
  };

  const IS_REVERSE_MAP = {
    RME_U_A_IS: 'IndustryStandard_RawMaterial_',
    MP_U_A_IS: 'IndustryStandard_MaterialProc_',
    MF_U_A_IS: 'IndustryStandard_MaterialDFP_',
    AF_U_A_IS: 'IndustryStandard_ArticleDFP_',
    M_U_A_IS: 'IndustryStandard_Manufacture_',
    TI_U_A_IS: 'IndustryStandard_Transport_',
    Pack_U_A_IS: 'IndustryStandard_LaDP_',
  };

  const transformed = { ...record };

  Object.keys(record).forEach(newKey => {
    // Parse pattern: {INDICATOR}_{SUFFIX}
    const parts = newKey.split('_');
    if (parts.length < 2) return;

    const indicator = parts[0];
    const indicatorOld = INDICATOR_REVERSE_MAP[indicator];

    if (!indicatorOld) return;

    // Check if it's an IS field (ends with _IS)
    if (newKey.endsWith('_IS')) {
      // Extract suffix without the indicator part: WS_RME_U_A_IS -> RME_U_A_IS
      const suffix = parts.slice(1).join('_');
      const oldPrefix = IS_REVERSE_MAP[suffix];

      if (oldPrefix) {
        const oldKey = `${oldPrefix}${indicatorOld}`;
        transformed[oldKey] = record[newKey];
        delete transformed[newKey];
      }
    } else {
      // Regular field: extract suffix after indicator
      const suffix = parts.slice(1).join('_');
      const oldPrefix = STAGE_REVERSE_MAP[suffix];

      if (oldPrefix !== undefined) {
        const oldKey = oldPrefix === '' ? indicatorOld : `${oldPrefix}${indicatorOld}`;
        transformed[oldKey] = record[newKey];
        delete transformed[newKey];
      }
    }
  });

  return transformed;
};

/**
 * Array con los 'resource' que requieren endpoint con /:accountId,
 * sin tocar la lógica especial de 'articles' ya existente en getList.
 */
const resourcesWithAccountId = [
  'materialProcess',
  'materialExtraction',
  'materialProcessing',
  'materialFinishing',
  'materialCertificate',
  'supplier',
  'supplierCertificate',
  'retailPackaging',
  'articlesHazardou',
  'articleHazardous',
  'articleMaterial',
  'articleSupplier',
  'articlePackaging',
  'articleProcess',
  'articleProcessManufacturing',
  'articleProcessFinishing',
  'articleProcessTrimming',
  'articlesummary',
  'ecommerceReference',
  'accountAgecSummary',
  'projects',
  'library',
  'library/adminLibrary',
  'accountFeatures',
  'material',
  'views',
  'articles',
  // Project tables (share same endpoints as their base resources via resourceMapping)
  'articleProject',
  'materialProject',
  'supplierProject',
  'retailPackagingProject',
  'articleDpp',
  // Account-scoped Data Seamless translations → /data-seamless/catalog-aliases/:accountId
  // (matched on the mapped apiResource, which is what getList/getOne check).
  'data-seamless/catalog-aliases',
  'project-packagings/secondary',
  'project-packagings/tertiary',
];

/**
 * Este provider mantiene la lógica previa y solo se añaden los ajustes
 * estrictamente necesarios para los nuevos endpoints con /:accountId.
 */
export const createDataProvider = (apiUrl, httpClient = fetchUtils.fetchJson, tokenOps) => ({

  getList: (resource, params) => {

    if (resource === 'articleCircularityMetrics') {
      const url = buildCircularityArticleMetricsUrl(apiUrl, params);
      return httpClient(url).then(({ json }) =>
        normalizeCircularityArticleMetricsResponse(json)
      );
    }


    // Data Seamless searchable grids (process-applicability, weight-shares): these
    // custom endpoints return a plain array and don't parse the CRUD filter[] format,
    // so translate the React-Admin search filter into a simple ?search= param.
    if (resource === 'processApplicability' || resource === 'weightShares') {
      const apiResource = resourceMapping[resource];
      const search = params.filter && (params.filter.q || params.filter.search);
      const qs = search ? `?search=${encodeURIComponent(search)}` : '';
      const url = `${apiUrl}/${apiResource}${qs}`;
      return httpClient(url).then(({ json }) => normalizeResponseData(json, resource));
    }

    // Field-definitions audit: the coverage endpoint returns the whole view unfiltered
    // (SELECT * FROM v_field_definition_assumption_coverage), so we apply React-Admin's
    // filter/sort/pagination client-side here. Dataset is small (~one row per field def).
    // displayName → case-insensitive substring; every other filter → exact match.
    if (resource === 'fieldDefinitionsCoverage') {
      const apiResource = resourceMapping[resource];
      return httpClient(`${apiUrl}/${apiResource}`).then(({ json }) => {
        let rows = Array.isArray(json) ? json : (json && json.data) || [];
        const f = params.filter || {};
        rows = rows.filter((r) => {
          if (f.displayName) {
            const hay = String(r.displayName || '').toLowerCase();
            if (!hay.includes(String(f.displayName).toLowerCase())) return false;
          }
          if (f.section && r.section !== f.section) return false;
          if (f.level && r.level !== f.level) return false;
          for (const key of ['hasAssumption', 'isCovered', 'hasMismatch']) {
            if (f[key] !== undefined && f[key] !== '' && String(!!r[key]) !== String(f[key])) return false;
          }
          return true;
        });

        const { field, order } = params.sort || {};
        if (field) {
          const dir = order === 'DESC' ? -1 : 1;
          rows = [...rows].sort((a, b) => {
            const av = a[field], bv = b[field];
            if (av == null && bv == null) return 0;
            if (av == null) return -dir;
            if (bv == null) return dir;
            return av < bv ? -dir : av > bv ? dir : 0;
          });
        }

        const total = rows.length;
        const { page, perPage } = params.pagination || { page: 1, perPage: 50 };
        const start = (page - 1) * perPage;
        return { data: rows.slice(start, start + perPage), total };
      });
    }

    // Caso especial para defaultMaterialProcessing y defaultMaterialFinishing: los
    // filtros Raw Material Category y Material Category son independientes en la UI
    // (rawMaterialCategoryId / materialCategoryId), pero ambas tablas solo tienen la FK
    // rawMaterialCategoryMaterialCategoryId y sus endpoints no exponen joins, así que no
    // se puede filtrar por campos de la relación. Resolvemos primero los ids de los
    // pares de rawMaterialCategoryMaterialCategory que cumplen los filtros elegidos y
    // consultamos la tabla con rawMaterialCategoryMaterialCategoryId||$in||<ids>.
    if (
      (resource === 'defaultMaterialProcessing' || resource === 'defaultMaterialFinishing') &&
      params.filter &&
      (params.filter.rawMaterialCategoryId || params.filter.materialCategoryId)
    ) {
      const { rawMaterialCategoryId, materialCategoryId, ...restFilter } = params.filter;

      const pairFilter = [];
      if (rawMaterialCategoryId) {
        pairFilter.push(`rawMaterialCategoryId||$eq||${rawMaterialCategoryId}`);
      }
      if (materialCategoryId) {
        pairFilter.push(`materialCategoryId||$eq||${materialCategoryId}`);
      }

      // Con filtro, este endpoint devuelve todas las filas que matchean (array plano)
      const pairUrl = `${apiUrl}/rawMaterialCategoryMaterialCategory?${composeQueryParams({ filter: pairFilter })}`;

      return httpClient(pairUrl).then(({ json }) => {
        const pairs = Array.isArray(json) ? json : (json && json.data) || [];
        const pairIds = pairs.map((pair) => pair.id);

        if (pairIds.length === 0) {
          return { data: [], total: 0 };
        }

        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {
          limit: perPage,
          page,
          sort: [`${field},${order}`],
          filter: composeFilter(
            { ...restFilter, rawMaterialCategoryMaterialCategoryId: pairIds },
            resource
          ),
        };

        const url = `${apiUrl}/${resource}?${composeQueryParams(query)}`;
        return httpClient(url).then(({ json: listJson }) => normalizeResponseData(listJson, resource));
      });
    }

    // Usar el nombre mapeado del recurso si existe
    const apiResource = resourceMapping[resource] || resource;

    // Special case for library resource - use adminLibrary endpoint for GET requests
    const effectiveResource = apiResource === 'library' ? 'library/adminLibrary' : apiResource;

    // Construimos el query string base
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    // Estos endpoints devuelven los datos ya ordenados server-side (isActive + name);
    // si mandamos sort el backend lo aplica y rompe ese orden.
    const NO_SORT_RESOURCES = ['database-version', 'lca-methodology-version', 'circularity-version'];

    let query = {
      limit: perPage,
      page: page,
    };
    if (!NO_SORT_RESOURCES.includes(effectiveResource)) {
      query.sort = [`${field},${order}`];
    }

    // Agregamos joins si existen para el recurso
    if (effectiveResource !== 'process' && Object.keys(joinResources).includes(effectiveResource)) {
      query.join = joinResources[effectiveResource];
    }

    // Agregamos filtros si existen
    let { filter } = params;

    // processByAccount/*: cuando el filtro Type está en "All" (sin type o type="all"),
    // la pantalla debe mostrar solo origins asignables a cuentas. Inyectamos
    // type IN [Custom, Specific] para excluir los tipos que no se muestran acá
    // (Global/Generic). Si el usuario eligió Custom o Specific, respetamos su selección.
    if (
      typeof resource === 'string' &&
      resource.startsWith('processByAccount/') &&
      (!filter || !filter.type || filter.type === 'all')
    ) {
      filter = { ...(filter || {}), type: ['Custom', 'Specific'] };
    }

    if (filter) {
      query.filter = composeFilter(filter, effectiveResource);
    }

    if (effectiveResource === 'filterAccountBestPractices') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        return Promise.reject('No accountId found');
      }

      const queryString = composeQueryParams({ ...query, accountId });
      return httpClient(`${apiUrl}/${effectiveResource}?${queryString}`)
        .then(({ json }) => normalizeResponseData(json, effectiveResource));
    }

    // Lista de recursos que devuelven arrays directamente
    const arrayResources = ['rawMaterialCategory'];

    // Caso especial para calculationLog
    if (effectiveResource === 'calculationLog') {
      // Convertir el array de sort a string simple para este recurso
      const modifiedQuery = { ...query };
      if (modifiedQuery.sort && Array.isArray(modifiedQuery.sort) && modifiedQuery.sort.length > 0) {
        modifiedQuery.sort = modifiedQuery.sort[0];
      }

      const queryString = composeQueryParams(modifiedQuery);
      const url = queryString ? `${apiUrl}/${effectiveResource}?${queryString}` : `${apiUrl}/${effectiveResource}`;


      return httpClient(url).then(({ json }) => {

        // Normalizar la respuesta
        const normalized = normalizeResponseData(json, 'calculationLog');

        // Asegurar que cada registro tenga un campo id único
        const dataWithIds = normalized.data.map((record, index) => {
          // Si no tiene id, intentar usar calculationLogId u otro campo común
          if (!record.id) {
            return {
              ...record,
              id: record.calculationLogId || record._id || `calc-log-${params.pagination.page}-${index}`
            };
          }
          return record;
        });


        return {
          data: dataWithIds,
          total: normalized.total
        };
      }).catch(error => {
        console.error('Error fetching calculationLog data:', error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Caso especial para library para asegurar un formato de respuesta consistente
    if (apiResource === 'library') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        console.warn(`[DEBUG] getList -> Resource: library -> No accountId found; using fallback URL`);
        return Promise.reject('No accountId found');
      }

      const queryString = composeQueryParams(query);
      // Usamos library/adminLibrary como endpoint para GET
      const url = queryString ? `${apiUrl}/library/adminLibrary/${accountId}?${queryString}` : `${apiUrl}/library/adminLibrary/${accountId}`;


      return httpClient(url).then(({ json }) => {

        // Usar la función normalizeResponseData para procesar la respuesta
        return normalizeResponseData(json, 'library');
      }).catch(error => {
        console.error('Error fetching library data:', error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Caso especial para transportOrigin que tiene una estructura diferente
    if (effectiveResource === 'transportOrigin') {
      // Usamos processOrigin/transport como ruta de API pero mantenemos transportOrigin como nombre de recurso
      const apiResource = 'processOrigin/transport';
      const queryString = composeQueryParams(query);
      const url = queryString ? `${apiUrl}/${apiResource}?${queryString}` : `${apiUrl}/${apiResource}`;

      return httpClient(url).then(({ json }) => {
        // Usar la función normalizeResponseData para procesar la respuesta
        return normalizeResponseData(json, 'transportOrigin');
      }).catch(error => {
        console.error('Error fetching transportOrigin data:', error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Caso especial para transportDistances que tiene una estructura diferente
    if (effectiveResource === 'transportDistances') {
      const queryString = composeQueryParams(query);
      const url = queryString ? `${apiUrl}/${effectiveResource}?${queryString}` : `${apiUrl}/${effectiveResource}`;


      return httpClient(url).then(({ json }) => {

        // Usar la función normalizeResponseData para procesar la respuesta
        return normalizeResponseData(json, 'transportDistances');
      }).catch(error => {
        console.error('Error fetching transportDistances data:', error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Caso especial para finishing y manufacturing
    if (resource === 'finishing' || resource === 'manufacturing') {
      const queryString = composeQueryParams(query);
      const url = queryString ? `${apiUrl}/${effectiveResource}?${queryString}` : `${apiUrl}/${effectiveResource}`;


      return httpClient(url).then(({ json }) => {

        // Usar la función normalizeResponseData para procesar la respuesta
        return normalizeResponseData(json, resource);
      }).catch(error => {
        console.error(`Error fetching ${resource} data:`, error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Caso especial para articles que tiene estructura similar a transportDistances
    if (effectiveResource === 'articles') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        console.warn(`[DEBUG] getList -> Resource: articles -> No accountId found; using fallback URL`);
        return Promise.reject('No accountId found');
      }

      // Verificar si hay un filtro por nombre
      const hasNameFilter = query.filter && Array.isArray(query.filter) &&
        query.filter.some(filter => {
          return filter.includes('name||$like||');
        });

      // Sólo eliminar los parámetros de paginación, límite y ordenamiento si hay búsqueda por nombre
      const modifiedQuery = { ...query };
      if (hasNameFilter) {
        delete modifiedQuery.limit;
        delete modifiedQuery.page;
        delete modifiedQuery.sort;
      }

      const queryString = composeQueryParams(modifiedQuery);
      const url = `${apiUrl}/articles/${accountId}?${queryString}`;


      return httpClient(url).then(({ json }) => {

        // Verificar la estructura de la respuesta y extraer los datos correctamente
        if (!json) {
          console.error('No data received from articles endpoint');
          return {
            data: [],
            total: 0,
          };
        }

        // Si la respuesta es un array directo (como sucede en búsquedas por nombre)
        if (Array.isArray(json)) {
          return {
            data: json,
            total: json.length
          };
        }

        // Asegurarse de que los datos tengan la estructura correcta para React-Admin
        // y que el total se extraiga correctamente para la paginación
        return {
          data: json.data || [],
          total: json.total || 0,  // Usar el valor 'total' para la paginación
        };
      }).catch(error => {
        console.error('Error fetching articles data:', error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Caso especial para materialExtraction, materialProcessing y materialFinishing
    if (effectiveResource === 'materialExtraction' || effectiveResource === 'materialProcessing' || effectiveResource === 'materialFinishing') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        console.warn(`[DEBUG] getList -> Resource: ${effectiveResource} -> No accountId found`);
        return Promise.reject('No accountId found');
      }

      // The backend paginates and returns `total` also when filters are applied,
      // so pagination and sort are always sent.
      const queryString = composeQueryParams(query);
      const url = queryString ? `${apiUrl}/${effectiveResource}/${accountId}?${queryString}` : `${apiUrl}/${effectiveResource}/${accountId}`;


      return httpClient(url).then(({ json }) => {
        // Usar la función de normalización para procesar la respuesta
        return normalizeResponseData(json, effectiveResource);
      }).catch(error => {
        console.error(`Error fetching ${effectiveResource} data:`, error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Caso especial para origins (usado como referencia)
    if (resource === 'origins') {
      // Modificar el filtro para usar 'name' en lugar de 'q'
      if (query.filter && Array.isArray(query.filter)) {
        // Buscar filtros que contengan 'q||$like||' y cambiarlos a 'name||$like||'
        query.filter = query.filter.map(filter => {
          if (filter.includes('q||$like||')) {
            return filter.replace('q||$like||', 'name||$like||');
          }
          return filter;
        });
      }

      // Construir la URL sin incluir accountId
      const queryString = composeQueryParams(query);
      const url = queryString ? `${apiUrl}/${resource}?${queryString}` : `${apiUrl}/${resource}`;


      return httpClient(url).then(({ json }) => {

        // Origins devuelve directamente un array de objetos
        if (Array.isArray(json)) {
          return {
            data: json,
            total: json.length
          };
        }

        // Por si acaso también maneja el formato objeto con propiedad data
        if (json && json.data) {
          return {
            data: json.data,
            total: json.count || json.total || json.data.length,
          };
        }

        // Caso de fallback
        console.error(`No valid data received from ${resource} endpoint`);
        return {
          data: [],
          total: 0,
        };
      }).catch(error => {
        console.error(`Error fetching ${resource} data:`, error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Caso especial para manufacturing y finishing (usados como referencias)
    if (resource === 'manufacturing' || resource === 'finishing') {
      // Modificar el filtro para usar 'name' en lugar de 'q'
      if (query.filter && Array.isArray(query.filter)) {
        // Buscar filtros que contengan 'q||$like||' y cambiarlos a 'name||$like||'
        query.filter = query.filter.map(filter => {
          if (filter.includes('q||$like||')) {
            return filter.replace('q||$like||', 'name||$like||');
          }
          return filter;
        });
      }

      // Construir la URL sin incluir accountId
      const queryString = composeQueryParams(query);
      const url = queryString ? `${apiUrl}/process/${resource}?${queryString}` : `${apiUrl}/process/${resource}`;


      return httpClient(url).then(({ json }) => {
        if (!json || !json.data) {
          console.error(`No data received from ${resource} endpoint`);
          return {
            data: [],
            total: 0,
          };
        }

        return {
          data: json.data,
          total: json.count || json.total || (json.data ? json.data.length : 0),
        };
      }).catch(error => {
        console.error(`Error fetching ${resource} data:`, error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Este bloque se ha eliminado para evitar duplicación. La lógica para articles 
    // ahora está unificada en el bloque anterior.

    // Caso especial para supplier (usado como referencia)
    if (resource === 'supplier') {
      // Modificar el filtro para usar 'name' en lugar de 'q'
      if (query.filter && Array.isArray(query.filter)) {
        // Buscar filtros que contengan 'q||$like||' y cambiarlos a 'name||$like||'
        query.filter = query.filter.map(filter => {
          if (filter.includes('q||$like||')) {
            return filter.replace('q||$like||', 'name||$like||');
          }
          return filter;
        });
      }

      // Construir la URL sin accountId (account-suppliers ya es específico de cuenta)
      const queryString = composeQueryParams(query);
      const url = queryString ? `${apiUrl}/${apiResource}?${queryString}` : `${apiUrl}/${apiResource}`;

      return httpClient(url).then(({ json }) => {
        if (!json || !json.data) {
          console.error(`No data received from ${resource} endpoint`);
          return {
            data: [],
            total: 0,
          };
        }

        return {
          data: json.data,
          total: json.count || json.total || (json.data ? json.data.length : 0),
        };
      }).catch(error => {
        console.error(`Error fetching ${resource} data:`, error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Caso especial para transport que debe usar process/transport como endpoint
    if (effectiveResource === 'transport') {
      // Usamos process/transport como ruta de API pero mantenemos transport como nombre de recurso
      const apiResource = 'process/transport';
      const queryString = composeQueryParams(query);
      const url = queryString ? `${apiUrl}/${apiResource}?${queryString}` : `${apiUrl}/${apiResource}`;

      return httpClient(url).then(({ json }) => {

        if (!json || !json.data) {
          console.error('No data received from transport endpoint');
          return {
            data: [],
            total: 0,
          };
        }

        // Asegurarse de que los datos tengan la estructura correcta para React-Admin
        return {
          data: json.data,
          total: json.count || json.total || (json.data ? json.data.length : 0),
        };
      }).catch(error => {
        console.error('Error fetching transport data:', error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Caso especial para recursos que requieren quitar paginación al buscar por nombre
    if (resource === 'articleProcessManufacturing' || resource === 'articleProcessFinishing' || resource === 'articleProcessTrimming' ||
      resource === 'articleMaterial' || resource === 'articlePackaging' || resource === 'articlesummary' ||
      resource === 'digitalizationData') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        console.warn(`[DEBUG] getList -> Resource: ${resource} -> No accountId found`);
        return Promise.reject('No accountId found');
      }

      // Mapear los nombres de recursos a las rutas de API correctas
      let apiPath;
      if (resource === 'articleProcessManufacturing') {
        apiPath = 'articleManufacturing';
      } else if (resource === 'articleProcessFinishing') {
        apiPath = 'articleFinishing';
      } else if (resource === 'articleProcessTrimming') {
        apiPath = 'articleTrimming';
      } else {
        apiPath = resource; // Para el resto usar el mismo nombre
      }

      // Verificar si hay un filtro por nombre (article.name, name o cualquier campo con $like)
      const hasNameFilter = query.filter && Array.isArray(query.filter) &&
        query.filter.some(filter => {
          return filter.includes('article.name||$like||') ||
            filter.includes('name||$like||') ||
            filter.includes('||$like||');
        });

      // Sólo eliminar los parámetros de paginación y ordenamiento si hay búsqueda por nombre.
      // Article process tables, articleMaterial, articlePackaging and articlesummary paginate
      // with filters in the backend.
      const paginatesWithFilters = ['articleProcessManufacturing', 'articleProcessFinishing',
        'articleProcessTrimming', 'articleMaterial', 'articlePackaging', 'articlesummary'].includes(resource);
      const modifiedQuery = { ...query };
      if (hasNameFilter && !paginatesWithFilters) {
        delete modifiedQuery.limit;
        delete modifiedQuery.page;
        delete modifiedQuery.sort;
      }

      const queryString = composeQueryParams(modifiedQuery);
      const url = queryString ? `${apiUrl}/${apiPath}/${accountId}?${queryString}` : `${apiUrl}/${apiPath}/${accountId}`;


      return httpClient(url).then(({ json }) => {

        // Verificar la estructura de la respuesta y extraer los datos correctamente
        if (!json) {
          console.error(`No data received from ${resource} endpoint`);
          return {
            data: [],
            total: 0,
          };
        }

        // Si la respuesta es un array directo (como sucede en búsquedas por nombre)
        if (Array.isArray(json)) {
          return {
            data: json,
            total: json.length
          };
        }

        // Asegurarse de que los datos tengan la estructura correcta para React-Admin
        // y que el total se extraiga correctamente para la paginación
        return {
          data: json.data || [],
          total: json.total || 0,  // Usar el valor 'total' para la paginación
        };
      }).catch(error => {
        console.error(`Error fetching ${resource} data:`, error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Caso especial para materialExtraction, materialProcessing y materialFinishing
    // Implementación dedicada para evitar problemas con la paginación
    if (resource === 'materialExtraction' || resource === 'materialProcessing' || resource === 'materialFinishing') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        console.warn(`[DEBUG] getList -> Resource: ${resource} -> No accountId found`);
        return Promise.reject('No accountId found');
      }

      const queryString = composeQueryParams(query);
      const url = queryString ? `${apiUrl}/${resource}/${accountId}?${queryString}` : `${apiUrl}/${resource}/${accountId}`;


      return httpClient(url).then(({ json }) => {

        // Verificar la estructura de la respuesta y extraer los datos correctamente
        if (!json) {
          console.error(`No data received from ${resource} endpoint`);
          return {
            data: [],
            total: 0,
          };
        }

        // Para debugging: mostrar detalladamente la estructura de paginación

        // Extraer explícitamente el total
        let totalCount = 0;

        if (json.total !== undefined && json.total !== null) {
          totalCount = parseInt(json.total, 10);
        } else if (json.count !== undefined && json.count !== null) {
          totalCount = parseInt(json.count, 10);
        } else if (json.data) {
          totalCount = json.data.length;
        }

        // Construir manualmente la respuesta esperada por React-Admin
        const result = {
          data: Array.isArray(json.data) ? json.data : [],
          total: totalCount
        };

        return result;
      }).catch(error => {
        console.error(`Error fetching ${resource} data:`, error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Para adminViews, mantenemos el formato original con accountId como query param
    if (effectiveResource === 'views/adminViews') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        console.warn('[DEBUG] getList -> No accountId found for adminViews');
        return Promise.reject('No accountId found');
      }

      const queryString = composeQueryParams({
        ...query,
        accountId,
      });

      const url = `${apiUrl}/${effectiveResource}?${queryString}`;

      return httpClient(url).then(({ json }) => {
        if (!json.data) {
          return {
            data: [],
            total: 0,
          };
        }
        return {
          data: json.data,
          total: json.total || json.data.length,
        };
      });
    }

    // Manejo especial para el recurso users
    if (apiResource === 'users') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        return Promise.reject('No accountId found');
      }

      const queryString = composeQueryParams(query);
      const urlWithAccount = queryString
        ? `${apiUrl}/${apiResource}/fromAccount/${accountId}?${queryString}`
        : `${apiUrl}/${apiResource}/fromAccount/${accountId}`;

      return httpClient(urlWithAccount).then(({ json }) => {
        // Si la respuesta es un array directo
        if (Array.isArray(json)) {
          return {
            data: json,
            total: json.length,
          };
        }

        if (!json.data) {
          return {
            data: [],
            total: 0,
          };
        }
        return {
          data: json.data,
          total: json.total || json.data.length,
        };
      }).catch(error => {
        console.error('Error fetching users data:', error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // El manejo especial para articleProcessManufacturing y articleProcessFinishing ahora se hace
    // en una sola implementación arriba en el código (líneas ~538-603), que incluye
    // la lógica para eliminar parámetros de paginación cuando se busca por nombre

    // Ya no hay caso especial para ecommerceReference en getList
    // Ahora usará el accountId como los demás recursos en resourcesWithAccountId

    // Caso especial para projects: el endpoint /projects/:accountId devuelve un array
    // plano (sin total), por lo que React-Admin tomaba total = data.length = perPage
    // y la paginación mostraba "10 of 10" sin posibilidad de avanzar de página.
    // Usamos en su lugar /projects/adminProjects, que devuelve
    // { data, count, total, page, pageCount } con el total real calculado
    // server-side (getManyAndCount), habilitando la paginación correcta.
    if (apiResource === 'projects') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        console.warn('[DEBUG] getList -> Resource: projects -> No accountId found');
        return Promise.reject('No accountId found');
      }

      const queryString = composeQueryParams({ ...query, accountId });
      const url = `${apiUrl}/projects/adminProjects?${queryString}`;


      return httpClient(url).then(({ json }) => {
        // normalizeResponseData usa json.total cuando está presente
        return normalizeResponseData(json, 'projects');
      }).catch(error => {
        console.error('Error fetching projects data:', error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Para recursos que necesitan accountId en la URL
    if (resourcesWithAccountId.includes(effectiveResource) || effectiveResource === 'articles') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        console.warn(`[DEBUG] getList -> Resource: ${effectiveResource} -> No accountId found; using fallback URL`);
      } else {


        const queryString = composeQueryParams(query);
        const urlWithAccount = queryString
          ? `${apiUrl}/${effectiveResource}/${accountId}?${queryString}`
          : `${apiUrl}/${effectiveResource}/${accountId}`;

        return httpClient(urlWithAccount).then(({ json }) => {
          // Si la respuesta es un array directo
          if (Array.isArray(json)) {
            return {
              data: json,
              total: json.length,
            };
          }

          if (!json.data) {
            return {
              data: [],
              total: 0,
            };
          }
          return {
            data: json.data,
            total: json.total || json.data.length,
          };
        });
      }
    }

    // Caso especial para article-impact/* (lca, ecoscore, circularity, ecobalyse)
    if (ARTICLE_IMPACT_RESOURCES.includes(resource)) {
      const queryString = composeQueryParams(query);
      // effectiveResource ya tiene el path real via resourceMapping (ej: 'article-impact/lca')
      const url = queryString ? `${apiUrl}/${effectiveResource}?${queryString}` : `${apiUrl}/${effectiveResource}`;

      return httpClient(url).then(({ json }) => {
        const normalized = normalizeResponseData(json, resource);

        // Transform LCA data from old to new nomenclature
        if (resource === 'articleImpactLca' && normalized.data) {
          normalized.data = normalized.data.map(transformArticleLcaData);
        }

        return normalized;
      }).catch((error) => {
        console.error(`Error fetching ${resource}:`, error);
        return { data: [], total: 0 };
      });
    }

    // URL fallback para recursos que no necesitan accountId
    const queryString = composeQueryParams(query);
    const url = queryString ? `${apiUrl}/${effectiveResource}?${queryString}` : `${apiUrl}/${effectiveResource}`;

    const processOriginSub = typeof effectiveResource === 'string' && effectiveResource.startsWith('processOrigin/')
      ? effectiveResource.split('/')[1]
      : null;

    return httpClient(url).then(({ json }) => {
      // processOrigin/* expose `databaseVersionId` nested under the process entity
      // (e.g. record.extraction.databaseVersionId). Flatten it so DbVersionField
      // (source="databaseVersionId") resolves it directly.
      const flattenProcessOrigin = (rows) => processOriginSub
        ? rows.map(r => ({
            ...r,
            databaseVersionId: r.databaseVersionId || (r[processOriginSub] && r[processOriginSub].databaseVersionId),
          }))
        : rows;

      // Si es un recurso que devuelve array directo o la respuesta es un array
      if (arrayResources.includes(effectiveResource) || Array.isArray(json)) {
        const arr = Array.isArray(json) ? flattenProcessOrigin(json) : [];
        return {
          data: arr,
          total: arr.length,
        };
      }

      if (!json.data) {
        return {
          data: [],
          total: 0,
        };
      }

      const data = flattenProcessOrigin(json.data);

      return {
        data,
        total: json.total || data.length,
      };
    });
  },

  getOne: (resource, params) => {

    // Caso especial para calculationLog
    if (resource === 'calculationLog') {
      const queryParams = {
        [`filter[0]`]: `id||$eq||${params.id}`
      };

      const queryString = stringify(queryParams);
      const url = `${apiUrl}/calculationLog?${queryString}`;


      return httpClient(url).then(({ json }) => {

        // Si los datos vienen como array
        if (json && Array.isArray(json)) {
          if (json.length > 0) {
            const record = json[0];
            // Asegurar que tenga id
            if (!record.id) {
              record.id = record.calculationLogId || record._id || params.id;
            }
            return { data: record };
          } else {
            return { data: {} };
          }
        }

        // Si los datos vienen con propiedad data como array
        if (json && json.data && Array.isArray(json.data)) {
          if (json.data.length > 0) {
            const record = json.data[0];
            // Asegurar que tenga id
            if (!record.id) {
              record.id = record.calculationLogId || record._id || params.id;
            }
            return { data: record };
          } else {
            return { data: {} };
          }
        }

        // Si los datos vienen directamente como objeto
        if (json && (json.id || json.calculationLogId)) {
          // Asegurar que tenga id
          if (!json.id) {
            json.id = json.calculationLogId || json._id || params.id;
          }
          return { data: json };
        }

        // Si los datos vienen con propiedad data como objeto
        if (json && json.data && (json.data.id || json.data.calculationLogId)) {
          const record = json.data;
          // Asegurar que tenga id
          if (!record.id) {
            record.id = record.calculationLogId || record._id || params.id;
          }
          return { data: record };
        }

        return { data: {} };
      }).catch((error) => {
        console.error('Error fetching calculationLog getOne:', error);
        return { data: {} };
      });
    }

    // Caso especial para transportModes
    if (resource === 'transportModes') {
      // Modificar la URL para usar el endpoint con filtro en lugar del endpoint directo
      // Esto nos asegura que obtendremos el mismo formato de respuesta que en getMany
      const queryParams = {
        [`filter[0]`]: `id||$eq||${params.id}`
      };

      const queryString = stringify(queryParams);
      const url = `${apiUrl}/transportModes?${queryString}`;

      return httpClient(url).then(({ json }) => {

        // Si los datos vienen como array
        if (json && Array.isArray(json)) {
          if (json.length > 0) {
            return { data: json[0] };
          } else {
            return { data: {} };
          }
        }

        // Si los datos vienen con propiedad data como array
        if (json && json.data && Array.isArray(json.data)) {
          if (json.data.length > 0) {
            return { data: json.data[0] };
          } else {
            return { data: {} };
          }
        }

        // Si los datos vienen directamente como objeto
        if (json && json.id) {
          return { data: json };
        }

        // Si los datos vienen con propiedad data como objeto
        if (json && json.data && json.data.id) {
          return { data: json.data };
        }

        return { data: {} };
      }).catch(() => {
        return { data: {} };
      });
    }

    // Usar el nombre mapeado del recurso si existe
    const apiResource = resourceMapping[resource] || resource;

    // Caso especial para transportOrigin
    if (apiResource === 'transportOrigin') {
      return httpClient(`${apiUrl}/processOrigin/transport/${params.id}`).then(({ json }) => {
        // Si tenemos transportId, obtener los datos del transporte
        if (json && json.transportId) {
          const transportId = json.transportId;

          // Obtener los datos del transporte usando el transportId
          return httpClient(`${apiUrl}/process/transport/${transportId}`)
            .then(({ json: transportJson }) => {
              // Añadir los datos del transporte al objeto principal
              json.transport = transportJson;

              // Si el transporte tiene transportModeId, añadirlo al objeto principal
              if (transportJson && transportJson.transportModeId) {
                json.transportModeId = transportJson.transportModeId;
              }

              return {
                data: json,
              };
            })
            .catch(() => {
              return {
                data: json,
              };
            });
        }

        // Si no hay transportId, simplemente devolver el objeto
        return {
          data: json,
        };
      });
    }

    // Caso especial para transportDistances
    if (apiResource === 'transportDistances') {
      return httpClient(`${apiUrl}/transportDistances/${params.id}`).then(({ json }) => {
        return {
          data: json,
        };
      });
    }

    // Caso especial para transport
    if (apiResource === 'transport') {
      return httpClient(`${apiUrl}/process/transport/${params.id}`).then(({ json }) => {
        return {
          data: json,
        };
      });
    }

    // Ya no hay caso especial para ecommerceReference en getOne
    // Ahora usará el accountId como los demás recursos en resourcesWithAccountId

    // Caso especial para project resources (articleProject, materialProject, supplierProject, retailPackagingProject)
    // Usan /{apiResource}/{accountId}/{id} consistente con el endpoint de lista /{apiResource}/{accountId}
    if (resource === 'articleProject' || resource === 'materialProject' ||
      resource === 'supplierProject' || resource === 'retailPackagingProject' ||
      resource === 'articleDpp') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        return Promise.reject('No accountId found');
      }
      const url = `${apiUrl}/${apiResource}/${accountId}/${params.id}`;


      return httpClient(url).then(({ json }) => {
        let record;
        // Direct array response: [{...}]
        if (Array.isArray(json)) {
          record = json.find(r => String(r.id) === String(params.id)) || json[0];
        // Wrapped array: { data: [...], total: N }
        } else if (json && json.data && Array.isArray(json.data)) {
          record = json.data.find(r => String(r.id) === String(params.id)) || json.data[0];
        // Wrapped single object: { data: {...} }
        } else if (json && json.data) {
          record = json.data;
        } else {
          record = json;
        }

        return { data: record || {} };
      });
    }

    // Caso especial para articleProcessManufacturing, articleProcessFinishing y accountFeatures
    if (resource === 'articleProcessManufacturing' || resource === 'articleProcessFinishing' || resource === 'articleProcessTrimming' || resource === 'accountFeatures') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        return Promise.reject('No accountId found');
      }

      // Mapear los nombres de recursos a las rutas de API correctas
      let apiPath = resource;
      if (resource === 'articleProcessManufacturing') {
        apiPath = 'articleManufacturing';
      } else if (resource === 'articleProcessFinishing') {
        apiPath = 'articleFinishing';
      } else if (resource === 'articleProcessTrimming') {
        apiPath = 'articleTrimming';
      }

      // Construir URL con el formato correcto: resource/:accountId?filters=id||$eq||:id
      const filters = `id||$eq||${params.id}`;
      const url = `${apiUrl}/${apiPath}/${accountId}?filters=${filters}`;


      return httpClient(url).then(({ json }) => {

        // Si la respuesta es un array, tomamos el primer elemento
        if (Array.isArray(json)) {
          if (json.length > 0) {
            return { data: json[0] };
          } else {
            return { data: {} };
          }
        }

        // Si la respuesta tiene la estructura esperada con data
        if (json && json.data) {
          if (Array.isArray(json.data)) {
            if (json.data.length > 0) {
              return { data: json.data[0] };
            } else {
              return { data: {} };
            }
          } else {
            return { data: json.data };
          }
        }

        return { data: json };
      });
    }



    // Caso especial para recursos account-* (articles, material, supplier, retailPackaging)
    // Estos endpoints ya son específicos de cuenta y usan formato simple /:id
    if (resource === 'articles' || resource === 'material' || resource === 'supplier' || resource === 'retailPackaging') {
      const url = `${apiUrl}/${apiResource}/${params.id}`;


      return httpClient(url).then(({ json }) => {

        // Si la respuesta ya es el objeto directo
        if (json && !json.data) {
          return { data: json };
        }

        // Si la respuesta tiene estructura con data
        if (json && json.data) {
          return { data: json.data };
        }

        return { data: json };
      });
    }

    // Caso especial para rawMaterialCategoryMaterialGroup
    if (resource === 'rawMaterialCategoryMaterialGroup') {
      // Construir URL con el formato correcto: rawMaterialCategoryMaterialGroup?filters=id||$eq||:id
      const filters = `id||$eq||${params.id}`;
      const url = `${apiUrl}/${apiResource}?filters=${filters}`;


      return httpClient(url).then(({ json }) => {

        // Si la respuesta es un array, tomamos el primer elemento
        if (Array.isArray(json)) {
          if (json.length > 0) {
            return { data: json[0] };
          } else {
            return { data: {} };
          }
        }

        // Si la respuesta tiene la estructura esperada con data
        if (json && json.data) {
          if (Array.isArray(json.data)) {
            if (json.data.length > 0) {
              return { data: json.data[0] };
            } else {
              return { data: {} };
            }
          } else {
            return { data: json.data };
          }
        }

        return { data: json };
      });
    }

    // Caso especial para articleHazardous: el backend NO expone
    // /articleHazardous/:accountId/:id (devuelve 404). Reutilizamos el
    // endpoint de lista account-scoped (que ya embebe el objeto `article`)
    // filtrando por id, con el mismo formato filter[0] que usa getList.
    if (resource === 'articleHazardous') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        return Promise.reject('No accountId found');
      }

      const queryString = composeQueryParams({
        filter: [`id||$eq||${params.id}`],
      });
      const url = `${apiUrl}/articleHazardous/${accountId}?${queryString}`;


      return httpClient(url).then(({ json }) => {
        if (Array.isArray(json)) {
          return { data: json[0] || {} };
        }
        if (json && Array.isArray(json.data)) {
          return { data: json.data[0] || {} };
        }
        if (json && json.data) {
          return { data: json.data };
        }
        return { data: json || {} };
      });
    }

    // Para recursos que requieren accountId en la URL
    if (resourcesWithAccountId.includes(apiResource)) {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        return Promise.reject('No accountId found');
      }

      // Nota: La lógica especial para articleProcessManufacturing y articleProcessFinishing
      // se ha movido a su propia sección antes de esta condición

      // Caso especial para materialExtraction, materialProcessing y materialFinishing
      // Estos recursos ahora usan /:accountId con filtros en query params
      if (apiResource === 'materialExtraction' ||
        apiResource === 'materialProcessing' ||
        apiResource === 'materialFinishing') {

        // Construir URL con el formato correcto: materialXXX/:accountId?filters=id||$eq||:id
        const filters = `id||$eq||${params.id}`;
        const url = `${apiUrl}/${apiResource}/${accountId}?filters=${filters}`;


        return httpClient(url).then(({ json }) => {

          // Si la respuesta es un array, tomamos el primer elemento
          if (Array.isArray(json)) {
            if (json.length > 0) {
              return { data: json[0] };
            } else {
              return { data: {} };
            }
          }

          // Si la respuesta tiene la estructura esperada con data
          if (json && json.data) {
            if (Array.isArray(json.data)) {
              if (json.data.length > 0) {
                return { data: json.data[0] };
              } else {
                return { data: {} };
              }
            } else {
              return { data: json.data };
            }
          }

          return { data: json };
        });
      }

      return httpClient(`${apiUrl}/${apiResource}/${accountId}/${params.id}`).then(({ json }) => {
        return {
          data: json,
        };
      });
    }

    // Caso especial para article-impact/*
    if (ARTICLE_IMPACT_RESOURCES.includes(resource)) {
      // apiResource already holds the real path via resourceMapping (e.g. 'article-impact/lca')
      return httpClient(`${apiUrl}/${apiResource}/${params.id}`).then(({ json }) => {
        // Normalizar estructura: el backend devuelve { article: {...}, project: {...}, ...impacts }
        // React Admin necesita el id en el nivel raíz y mantener las referencias anidadas
        let data = {
          ...json,
          id: params.id, // Usar el ID de la URL como ID principal
          article: json.article || {},
          project: json.project || {},
        };

        // Transform LCA data from old to new nomenclature
        if (resource === 'articleImpactLca') {
          data = transformArticleLcaData(data);
        }

        return { data };
      });
    }

    return httpClient(`${apiUrl}/${apiResource}/${params.id}`).then(({ json }) => {
      return {
        data: json,
      };
    });
  },

  getMany: (resource, params) => {
    // Caso especial para origins para asegurar que todos se carguen
    if (resource === 'origins') {
      const queryParams = {
        [`filter[0]`]: `id||$in||${params.ids.join(',')}`
      };

      const queryString = stringify(queryParams);
      const url = `${apiUrl}/${resource}?${queryString}`;

      // Función para obtener todas las páginas de resultados
      const fetchAllPages = async (initialUrl) => {
        let allData = [];
        let currentUrl = initialUrl;
        let hasMorePages = true;
        let currentPage = 1;

        while (hasMorePages) {
          try {
            const { json } = await httpClient(currentUrl);

            // Agregar los datos de esta página al resultado acumulado
            if (json && json.data && Array.isArray(json.data)) {
              allData = [...allData, ...json.data];
            } else if (json && Array.isArray(json)) {
              allData = [...allData, ...json];
            } else if (json && json.id) {
              allData.push(json);
            }

            // Verificar si hay más páginas
            const pageCount = json.pageCount || 1;
            const page = parseInt(json.page) || 1;

            if (page < pageCount) {
              // Construir URL para la siguiente página
              const nextPage = page + 1;
              const urlObj = new URL(currentUrl);
              urlObj.searchParams.set('page', nextPage);
              currentUrl = urlObj.toString();
              currentPage = nextPage;
            } else {
              hasMorePages = false;
            }
          } catch (error) {
            console.error(`Error fetching origins page ${currentPage}:`, error);
            hasMorePages = false;
          }
        }

        return allData;
      };

      // Usar Promise para mantener la interfaz esperada por React-Admin
      return fetchAllPages(url)
        .then(allData => {
          return { data: allData };
        })
        .catch(error => {
          console.error('[DEBUG] Error fetching origins in getMany:', error);
          return { data: [] };
        });
    }

    // Caso especial para articleCategory para asegurar que todos los datos estén disponibles
    if (resource === 'articleCategory') {
      const queryParams = {
        [`filter[0]`]: `id||$in||${params.ids.join(',')}`
      };

      const queryString = stringify(queryParams);
      const url = `${apiUrl}/${resource}?${queryString}`;


      // Función para obtener todas las páginas de resultados
      const fetchAllPages = async (initialUrl) => {
        let allData = [];
        let currentUrl = initialUrl;
        let hasMorePages = true;
        let currentPage = 1;

        while (hasMorePages) {
          try {
            const { json } = await httpClient(currentUrl);

            // Agregar los datos de esta página al resultado acumulado
            if (json && json.data && Array.isArray(json.data)) {
              allData = [...allData, ...json.data];
            } else if (json && Array.isArray(json)) {
              allData = [...allData, ...json];
            } else if (json && json.id) {
              allData.push(json);
            }

            // Verificar si hay más páginas
            const pageCount = json.pageCount || 1;
            const page = parseInt(json.page) || 1;

            if (page < pageCount) {
              // Construir URL para la siguiente página
              const nextPage = page + 1;
              const urlObj = new URL(currentUrl);
              urlObj.searchParams.set('page', nextPage);
              currentUrl = urlObj.toString();
              currentPage = nextPage;
            } else {
              hasMorePages = false;
            }
          } catch (error) {
            console.error(`Error fetching articleCategory page ${currentPage}:`, error);
            hasMorePages = false;
          }
        }

        return allData;
      };

      // Usar Promise para mantener la interfaz esperada por React-Admin
      return fetchAllPages(url)
        .then(allData => {
          return { data: allData };
        })
        .catch(error => {
          console.error('Error fetching all articleCategory pages:', error);
          return { data: [] };
        });
    }

    // Caso especial para transportModes
    if (resource === 'transportModes') {

      const queryParams = {
        [`filter[0]`]: `id||$in||${params.ids.join(',')}`
      };

      const queryString = stringify(queryParams);
      const url = `${apiUrl}/transportModes?${queryString}`;

      return httpClient(url).then(({ json }) => {
        // Si los datos vienen en formato de array simple (sin propiedad data)
        if (json && Array.isArray(json)) {
          // Si solo hay un elemento en el array y estamos buscando un solo ID, devolver ese elemento directamente
          if (json.length === 1 && params.ids.length === 1) {
            return { data: [json[0]] };
          }
          return { data: json };
        }

        // Si los datos vienen con propiedad data
        if (json && json.data && Array.isArray(json.data)) {
          return { data: json.data };
        }

        // Si es un objeto único (no un array)
        if (json && json.id) {
          return { data: [json] };
        }

        return { data: [] };
      }).catch(error => {
        console.error('Error fetching transportModes:', error);
        return { data: [] };
      });
    }

    const effectiveResource = resourceMapping[resource] || resource;

    // Caso especial para transport
    if (effectiveResource === 'transport') {
      // Usamos process/transport como ruta de API pero mantenemos transport como nombre de recurso
      const apiResource = 'process/transport';

      const queryParams = {
        [`filter[0]`]: `id||$in||${params.ids.join(',')}`
      };

      const queryString = stringify(queryParams);
      const url = `${apiUrl}/${apiResource}?${queryString}`;

      return httpClient(url).then(({ json }) => {

        // Si la respuesta es un array directamente (sin estar dentro de data)
        if (Array.isArray(json)) {
          return { data: json };
        }

        // Si la respuesta tiene la estructura esperada con data
        if (json && json.data) {
          return { data: json.data };
        }

        console.error('Unexpected response format from transport endpoint:', json);
        return { data: [] };
      }).catch(error => {
        console.error('Error fetching transport items:', error);
        return { data: [] };
      });
    }

    // Fallback for other resources
    const filters = {};
    filters[`filter[0]`] = `id||$in||${params.ids.join(',')}`;

    const queryString = new URLSearchParams(filters).toString();
    const url = `${apiUrl}/${effectiveResource}?${queryString}`;

    return httpClient(url).then(({ json }) => ({
      data: Array.isArray(json.data) ? json.data : (validateSupplier(url) ? [json] : Array.isArray(json) ? json : [])
    }));
  },

  getManyReference: (resource, params) => {
    const { target, id, pagination, sort, filter } = params;
    // Caso especial para articleCategory
    if (resource === 'articleCategory') {
      // Construir query con filtros
      const queryParams = {
        ...filter
      };

      if (target && id) {
        queryParams[`filter[0]`] = `${target}||$eq||${id}`;
      }

      // Añadir paginación y ordenamiento
      if (pagination) {
        queryParams.limit = pagination.perPage;
        queryParams.page = pagination.page;
      }

      if (sort) {
        queryParams.sort = `${sort.field},${sort.order}`;
      }

      const queryString = stringify(queryParams);
      const url = `${apiUrl}/${resource}?${queryString}`;


      return httpClient(url).then(({ json }) => {

        let data = [];
        let total = 0;

        // Si los datos vienen en formato de array simple (sin propiedad data)
        if (json && Array.isArray(json)) {
          data = json;
          total = json.length;
        }
        // Si los datos vienen con propiedad data
        else if (json && json.data && Array.isArray(json.data)) {
          data = json.data;
          total = json.count || json.total || json.data.length;
        }
        // Si es un objeto único (no un array)
        else if (json && json.id) {
          data = [json];
          total = 1;
        }

        return {
          data: data,
          total: total,
        };
      }).catch(error => {
        console.error('Error fetching articleCategory reference data:', error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    // Caso especial para transportModes
    if (resource === 'transportModes') {
      // Construir query con filtros
      const queryParams = {
        ...filter
      };

      if (target && id) {
        queryParams[`filter[0]`] = `${target}||$eq||${id}`;
      }

      // Añadir paginación y ordenamiento
      if (pagination) {
        queryParams.limit = pagination.perPage;
        queryParams.page = pagination.page;
      }

      if (sort) {
        queryParams.sort = `${sort.field},${sort.order}`;
      }

      const queryString = stringify(queryParams);
      const url = `${apiUrl}/transportModes?${queryString}`;

      return httpClient(url).then(({ json }) => {
        let data = [];
        let total = 0;

        // Si los datos vienen en formato de array simple (sin propiedad data)
        if (json && Array.isArray(json)) {
          data = json;
          total = json.length;
        }
        // Si los datos vienen con propiedad data
        else if (json && json.data && Array.isArray(json.data)) {
          data = json.data;
          total = json.count || json.total || json.data.length;
        }

        return {
          data: data,
          total: total,
        };
      }).catch(error => {
        console.error('Error fetching transportModes reference data:', error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    const query = {
      filter: { ...filter, [target]: id },
      sort: { field: sort.field, order: sort.order },
      pagination: { page: pagination.page, perPage: pagination.perPage },
    };

    // Caso especial para referencias a transport desde transportOrigin
    if (resource === 'transport' && target === 'transportId') {
      // Usamos process/transport como ruta de API pero mantenemos transport como nombre de recurso
      const apiResource = 'process/transport';
      const { page, perPage } = query.pagination;
      const { field, order } = query.sort;

      const apiQuery = {
        limit: perPage,
        page: page,
        sort: [`${field},${order}`],
      };

      if (query.filter) {
        apiQuery.filter = composeFilter(query.filter, resource);
      }

      const queryString = composeQueryParams(apiQuery);
      const url = queryString ? `${apiUrl}/${apiResource}?${queryString}` : `${apiUrl}/${apiResource}`;


      return httpClient(url).then(({ json }) => {

        if (!json || !json.data) {
          console.error('No data received from transport endpoint');
          return {
            data: [],
            total: 0,
          };
        }

        // Procesamos la estructura específica donde transport es un objeto directo
        const data = json.data.map(item => {
          // Si el item tiene una propiedad transport que es un objeto, lo usamos directamente
          if (item.transport && typeof item.transport === 'object') {
            return {
              ...item.transport,
              id: item.transport.id
            };
          }
          return item;
        });

        return {
          data: data,
          total: json.count || json.total || (json.data ? json.data.length : 0),
        };
      }).catch(error => {
        console.error('Error fetching transport reference data:', error);
        return {
          data: [],
          total: 0,
        };
      });
    }

    return this.getList(resource, query);
  },

  update: (resource, params) => {
    // Usar el nombre mapeado del recurso si existe
    const apiResource = resourceMapping[resource] || resource;

    // Caso especial para article-impact/*
    if (ARTICLE_IMPACT_RESOURCES.includes(resource)) {
      // Eliminar campos que el backend no espera (article, project, id, createdAt, updatedAt, accountId)
      const { article, project, id, createdAt, updatedAt, accountId, articleId, ...impactData } = params.data;

      // Transform data back to old nomenclature before sending to API
      let dataToSend = impactData;
      if (resource === 'articleImpactLca') {
        dataToSend = reverseTransformArticleLcaData(impactData);
      }

      // apiResource already holds the real path via resourceMapping (e.g. 'article-impact/lca')
      return httpClient(`${apiUrl}/${apiResource}/${params.id}`, {
        method: 'PUT',
        body: JSON.stringify(dataToSend),
      }).then(() => ({
        data: { ...params.data, id: params.id },
      }));
    }

    if (resource === 'circularityDefaults') {
      return httpClient(`${apiUrl}/${apiResource}/${params.id}`, {
        method: 'PUT',
        body: JSON.stringify({ default_value: params.data.default_value }),
      }).then(() => ({
        data: { ...params.data, id: params.id },
      }));
    }

    // Caso especial para articleProcessManufacturing y articleProcessFinishing
    if (resource === 'articleProcessManufacturing' || resource === 'articleProcessFinishing' || resource === 'articleProcessTrimming') {
      // Mapear los nombres de recursos a las rutas de API correctas
      let apiPath;
      if (resource === 'articleProcessManufacturing') apiPath = 'articleManufacturing';
      else if (resource === 'articleProcessFinishing') apiPath = 'articleFinishing';
      else apiPath = 'articleTrimming';

      // Para PUT no incluimos el accountId en la URL
      return httpClient(`${apiUrl}/${apiPath}/${params.id}`, {
        method: 'PUT',
        body: JSON.stringify(params.data),
      }).then(() => ({
        data: { ...params.data, id: params.id },
      }));
    }

    // Caso especial para ecommerceReference, library y projects
    if (resource === 'ecom-reference-article' || apiResource === 'ecommerceReference' || apiResource === 'library' || apiResource === 'projects') {
      return httpClient(`${apiUrl}/${apiResource}/${params.id}`, {
        method: 'PUT',
        body: JSON.stringify(params.data),
      }).then(() => ({
        data: { ...params.data, id: params.id },
      }));
    }

    // Caso especial para materialExtraction, materialProcessing y materialFinishing
    if (apiResource === 'materialExtraction' || apiResource === 'materialProcessing' || apiResource === 'materialFinishing') {
      return httpClient(`${apiUrl}/${apiResource}/${params.id}`, {
        method: 'PUT',
        body: JSON.stringify(params.data),
      }).then(() => ({
        data: { ...params.data, id: params.id },
      }));
    }

    // Caso especial para los recursos de proceso (extraction, processing, finishing, manufacturing, packaging, transport)
    if (resource === 'extraction' || resource === 'processing' || resource === 'finishing' ||
      resource === 'manufacturing' || resource === 'packaging' || resource === 'transport' || resource === 'trimming') {
      return httpClient(`${apiUrl}/${apiResource}/${params.id}`, {
        method: 'PUT',
        body: JSON.stringify(params.data),
      }).then(() => ({
        data: { ...params.data, id: params.id },
      }));
    }

    // Caso especial para recursos account-* (articles, material, supplier, retailPackaging)
    // Estos endpoints ya son específicos de cuenta y usan formato simple /:id
    if (resource === 'articles' || resource === 'material' || resource === 'supplier' || resource === 'retailPackaging') {
      return httpClient(`${apiUrl}/${apiResource}/${params.id}`, {
        method: 'PUT',
        body: JSON.stringify(params.data),
      }).then(() => ({
        data: { ...params.data, id: params.id },
      }));
    }

    // Caso especial para supplierProject: a diferencia de articles y material, el
    // controller de supplier no expone PUT /:accountId/:id, así que la URL con
    // accountId devuelve 404. Usamos la ruta CRUD estándar PUT /supplier/:id (la
    // misma que usa Historical Suppliers); el backend scopea por cuenta vía el
    // token. Se excluyen las relaciones embebidas que devuelve el GET (project,
    // origin, transport, etc.) para no intentar reescribirlas al guardar.
    if (resource === 'supplierProject') {
      const {
        account,
        project,
        accountSupplier,
        origin,
        transport,
        categoryId,
        supplierName,
        createdAt,
        updatedAt,
        ...supplierData
      } = params.data;

      return httpClient(`${apiUrl}/supplier/${params.id}`, {
        method: 'PUT',
        body: JSON.stringify(supplierData),
      }).then(() => ({
        data: { ...params.data, id: params.id },
      }));
    }

    // Si el recurso está en la lista de /:accountId, agregamos esa parte
    if (resourcesWithAccountId.includes(apiResource)) {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        return Promise.reject('No accountId found');
      }

      const url = `${apiUrl}/${apiResource}/${accountId}/${params.id}`;

      return httpClient(url, {
        method: 'PUT',
        body: JSON.stringify(params.data),
      }).then(() => ({
        data: { ...params.data, id: params.id },
      }));
    }

    const data = countDiff(params.data, params.previousData);
    const url = `${apiUrl}/${apiResource}/${params.id}`;

    return httpClient(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }).then(() => ({ data: params.data }));
  },

  updateMany: (resource, params) => {
    // Usar el nombre mapeado del recurso si existe
    const apiResource = resourceMapping[resource] || resource;

    return Promise.all(
      params.ids.map((id) => {
        const url = `${apiUrl}/${apiResource}/${id}`;
        return httpClient(url, {
          method: 'PUT',
          body: JSON.stringify(params.data),
        });
      })
    ).then((responses) => ({
      data: responses.map(({ json }) => json),
    }));
  },

  create: (resource, params) => {
    // Usar el nombre mapeado del recurso si existe
    const apiResource = resourceMapping[resource] || resource;

    // Caso especial: processByAccount/<type> → POST anidado al sub-recurso de cuentas.
    // El form envía { processOriginId, accountId } y se traduce a
    // POST /processOrigin/<type>/:processOriginId/accounts  body: { accountId }
    if (typeof resource === 'string' && resource.startsWith('processByAccount/')) {
      const type = resource.split('/')[1];
      const { processOriginId, accountId } = params.data || {};
      if (!processOriginId || !accountId) {
        return Promise.reject(new Error('processOriginId y accountId son requeridos'));
      }
      const url = `${apiUrl}/processOrigin/${type}/${processOriginId}/accounts`;
      // eslint-disable-next-line no-console
      return httpClient(url, {
        method: 'POST',
        body: JSON.stringify({ accountId }),
      }).then(({ json }) => {
        // eslint-disable-next-line no-console
        return {
          data: { id: (json && (json.id || `${processOriginId}-${accountId}`)), ...(json || {}), processOriginId, accountId },
        };
      });
    }

    // Caso especial para articleProcessManufacturing y articleProcessFinishing
    if (resource === 'articleProcessManufacturing' || resource === 'articleProcessFinishing' || resource === 'articleProcessTrimming') {
      // Mapear los nombres de recursos a las rutas de API correctas
      let apiPath;
      if (resource === 'articleProcessManufacturing') apiPath = 'articleManufacturing';
      else if (resource === 'articleProcessFinishing') apiPath = 'articleFinishing';
      else apiPath = 'articleTrimming';

      // Para POST no incluimos el accountId en la URL
      return httpClient(`${apiUrl}/${apiPath}`, {
        method: 'POST',
        body: JSON.stringify(params.data),
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    }

    // Caso especial para materialExtraction, materialProcessing y materialFinishing
    // Se elimina el accountId de la URL para POST requests como se requiere en la API
    if (apiResource === 'materialExtraction' || apiResource === 'materialProcessing' || apiResource === 'materialFinishing') {

      return httpClient(`${apiUrl}/${apiResource}`, {
        method: 'POST',
        body: JSON.stringify(params.data),
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    }

    // Caso especial para projects, library y accountFeatures - POST debe ir a /api/resource (sin accountId)
    if (apiResource === 'projects' || apiResource === 'accountFeatures' || apiResource === 'library') {
      const url = `${apiUrl}/${apiResource}`;

      return httpClient(url, {
        method: 'POST',
        body: JSON.stringify(params.data),
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    }

    // Caso especial para los recursos de proceso (extraction, processing, finishing, manufacturing, packaging, transport)
    if (resource === 'extraction' || resource === 'processing' || resource === 'finishing' ||
      resource === 'manufacturing' || resource === 'packaging' || resource === 'transport' || resource === 'trimming') {
      return httpClient(`${apiUrl}/${apiResource}`, {
        method: 'POST',
        body: JSON.stringify(params.data),
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    }

    // Caso especial para recursos account-* (articles, material, supplier, retailPackaging)
    // Estos endpoints ya son específicos de cuenta y no usan accountId en la URL
    if (resource === 'articles' || resource === 'material' || resource === 'supplier' || resource === 'retailPackaging') {
      return httpClient(`${apiUrl}/${apiResource}`, {
        method: 'POST',
        body: JSON.stringify(params.data),
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    }

    // Si el recurso está en la lista de /:accountId, agregamos esa parte
    if (resourcesWithAccountId.includes(apiResource)) {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        return Promise.reject('No accountId found');
      }

      const url = `${apiUrl}/${apiResource}/${accountId}`;

      return httpClient(url, {
        method: 'POST',
        body: JSON.stringify(params.data),
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id },
      }));
    }

    const url = `${apiUrl}/${apiResource}`;

    return httpClient(url, {
      method: 'POST',
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({
      data: { ...params.data, id: json.id },
    }));
  },

  delete: (resource, params) => {
    // Usar el nombre mapeado del recurso si existe
    const apiResource = resourceMapping[resource] || resource;

    // Caso especial para articleProcessManufacturing, articleProcessFinishing y accountFeatures
    if (resource === 'articleProcessManufacturing' || resource === 'articleProcessFinishing' || resource === 'articleProcessTrimming' || resource === 'accountFeatures') {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        return Promise.reject('No accountId found');
      }

      // Mapear los nombres de recursos a las rutas de API correctas
      let apiPath = resource;
      if (resource === 'articleProcessManufacturing') {
        apiPath = 'articleManufacturing';
      } else if (resource === 'articleProcessFinishing') {
        apiPath = 'articleFinishing';
      } else if (resource === 'articleProcessTrimming') {
        apiPath = 'articleTrimming';
      }

      // Para accountFeatures, probar un formato directo sin accountId
      if (resource === 'accountFeatures') {
        // Intentar URL directa sin accountId
        const url = `${apiUrl}/${apiPath}/${params.id}`;


        return httpClient(url, {
          method: 'DELETE',
        }).then(() => ({
          data: params.previousData,
        })).catch((error) => {
          console.error(`Error deleting ${resource} with direct URL:`, error);

          // Si falla, intentar con el formato alternativo que usa query parameters
          const filters = `id||$eq||${params.id}`;
          const altUrl = `${apiUrl}/${apiPath}/${accountId}?filters=${filters}`;


          return httpClient(altUrl, {
            method: 'DELETE',
          }).then(() => ({
            data: params.previousData,
          }));
        });
      } else {
        // Para los otros recursos, mantener el formato actual
        return httpClient(`${apiUrl}/${apiPath}/${accountId}/${params.id}`, {
          method: 'DELETE',
        }).then(() => ({
          data: params.previousData,
        }));
      }
    }

    // Caso especial para materialExtraction, materialProcessing y materialFinishing
    if (apiResource === 'materialExtraction' || apiResource === 'materialProcessing' || apiResource === 'materialFinishing') {
      return httpClient(`${apiUrl}/${apiResource}/${params.id}`, {
        method: 'DELETE',
      }).then(() => ({
        data: params.previousData,
      }));
    }

    // Caso especial para projects - DELETE debe ir a /api/projects/:id (sin accountId)
    if (apiResource === 'projects') {
      const url = `${apiUrl}/${apiResource}/${params.id}`;

      return httpClient(url, {
        method: 'DELETE',
      }).then(() => ({
        data: params.previousData,
      }));
    }

    // Caso especial para library - DELETE debe ir a /api/library/:id (sin accountId)
    if (apiResource === 'library') {
      const url = `${apiUrl}/${apiResource}/${params.id}`;

      return httpClient(url, {
        method: 'DELETE',
      }).then(() => {
        // Asegurarse de que la respuesta tenga el formato esperado por React-Admin
        return {
          data: params.previousData || { id: params.id }
        };
      }).catch(error => {
        console.error(`Error deleting library: ${error}`);
        // Aún en caso de error, devolvemos un formato válido para React-Admin
        return {
          data: params.previousData || { id: params.id }
        };
      });
    }

    // Caso especial para los recursos de proceso (extraction, processing, finishing, manufacturing, packaging, transport)
    if (resource === 'extraction' || resource === 'processing' || resource === 'finishing' ||
      resource === 'manufacturing' || resource === 'packaging' || resource === 'transport' || resource === 'trimming') {
      return httpClient(`${apiUrl}/${apiResource}/${params.id}`, {
        method: 'DELETE',
      }).then(() => ({
        data: params.previousData,
      }));
    }

    // Caso especial para recursos account-* (articles, material, supplier, retailPackaging)
    // Estos endpoints ya son específicos de cuenta y usan formato simple /:id
    if (resource === 'articles' || resource === 'material' || resource === 'supplier' || resource === 'retailPackaging') {
      return httpClient(`${apiUrl}/${apiResource}/${params.id}`, {
        method: 'DELETE',
      }).then(() => ({
        data: params.previousData,
      }));
    }

    // Si el recurso está en la lista de /:accountId, agregamos esa parte
    if (resourcesWithAccountId.includes(apiResource)) {
      const accountId = tokenOps?.account?.data?.accountId;
      if (!accountId) {
        return Promise.reject('No accountId found');
      }

      const url = `${apiUrl}/${apiResource}/${accountId}/${params.id}`;

      return httpClient(url, {
        method: 'DELETE',
      }).then(() => ({
        data: params.previousData,
      }));
    }

    const url = `${apiUrl}/${apiResource}/${params.id}`;

    return httpClient(url, {
      method: 'DELETE',
    }).then(() => ({
      data: params.previousData,
    }));
  },

  deleteMany: (resource, params) => {
    // Usar el nombre mapeado del recurso si existe
    const apiResource = resourceMapping[resource] || resource;

    return Promise.all(
      params.ids.map((id) => {
        const url = `${apiUrl}/${apiResource}/${id}`;

        return httpClient(url, {
          method: 'DELETE',
        });
      })
    ).then((responses) => ({ data: responses.map(({ json }) => json) }));
  },
});

export const useDataProvider = (tokenOps) => {
  return useMemo(() => {
    return createDataProvider(hostname, fetchJson(tokenOps.account?.accessToken), tokenOps);
  }, [tokenOps.account?.accessToken]);
};

export default useDataProvider;
